import type { AlgorithmConfig, Answers, MoviesFile, Question, StoredResult } from '../types'

interface ResultEngine {
  algorithm: AlgorithmConfig
  movies: MoviesFile['movies']
  questions: Question[]
  evaluate: typeof import('../algorithm/evaluate').evaluate
}

let enginePromise: Promise<ResultEngine> | null = null
const assetPromises = new Map<string, Promise<void>>()

export function preloadResultEngine() {
  if (!enginePromise) {
    enginePromise = Promise.all([
      import('../data/questions'),
      import('../data/algorithm.final.json'),
      import('../data/movies.scoring.generated.json'),
      import('../algorithm/evaluate'),
    ]).then(([questionsModule, algorithmModule, moviesModule, evaluatorModule]) => ({
      algorithm: algorithmModule.default as unknown as AlgorithmConfig,
      movies: (moviesModule.default as unknown as MoviesFile).movies,
      questions: questionsModule.questions,
      evaluate: evaluatorModule.evaluate,
    }))
  }
  return enginePromise
}

export async function calculateStoredResult(answers: Answers): Promise<StoredResult | null> {
  const engine = await preloadResultEngine()
  if (engine.questions.some((question) => !answers[question.id])) return null
  const evaluation = engine.evaluate(engine.questions, answers, engine.movies, engine.algorithm)
  return {
    movieId: evaluation.result.movie_id,
    match: evaluation.match,
    vector: evaluation.vector,
    radar: evaluation.radar,
    top3: evaluation.top3.map((row) => ({
      movieId: row.movie_id,
      title: row.title,
      score: row.final_score,
    })),
  }
}

export function getResultAssetUrls(movieId: string) {
  if (movieId === 'black_swan') {
    return [
      '/assets/result-filmstrip/black-swan-filmstrip-master-v4.webp',
      '/assets/result-filmstrip/black-swan-title-art-v3.webp',
      '/assets/result-filmstrip/black-swan-persona-art-v3.webp',
      '/assets/black-swan-feather-v3.webp',
    ]
  }
  return [
    `/assets/result-elements-v3/${movieId}/left.webp`,
    `/assets/result-elements-v3/${movieId}/right.webp`,
  ]
}

function preloadImage(src: string) {
  const existing = assetPromises.get(src)
  if (existing) return existing
  const pending = new Promise<void>((resolve) => {
    const image = new Image()
    image.decoding = 'async'
    image.fetchPriority = 'high'
    image.onload = () => resolve()
    image.onerror = () => resolve()
    image.src = src
  })
  assetPromises.set(src, pending)
  return pending
}

export function preloadResultAssets(movieId: string) {
  return Promise.all(getResultAssetUrls(movieId).map(preloadImage)).then(() => undefined)
}
